import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { format, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { safeParseDate } from '../../utils/dateUtils';
import { _t, _n } from '../../utils/i18n';

const safeFormat = (dateInput, formatString, options = {}) => {
  const date = safeParseDate(dateInput);
  if (!date) return _t('Invalid date');
  try {
    return format(date, formatString, options);
  } catch (e) {
    return _t('Format error');
  }
};

const DNProductPresence = ({ data, startDate, endDate }) => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const [showUnchangedStores, setShowUnchangedStores] = useState(false);
  const gridRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ startRow: 0, endRow: 50, startCol: 0, endCol: 30 });

  // Générer tous les jours de la période
  const days = useMemo(() => {
    if (!startDate || !endDate) return [];
    try {
      // Utiliser la fonction robuste pour créer les dates
      const start = safeParseDate(startDate);
      const end = safeParseDate(endDate);

      if (!start || !end) {
        console.warn(_t('Invalid start or end dates:'), startDate, endDate);
        return [];
      }

      return eachDayOfInterval({ start, end });
    } catch (error) {
      console.error(_t('Error generating days:'), error);
      return [];
    }
  }, [startDate, endDate]);

  // Calculer la DN globale et autres statistiques
  const { presenceMap, storesInfo, validStoresExist, globalStats } = useMemo(() => {
    if (!data || data.length === 0 || !days.length)
      return {
        presenceMap: {},
        storesInfo: {},
        validStoresExist: false,
        globalStats: { present: 0, total: 0, rate: 0 }
      };

    const map = {};
    const storeInfo = {};
    let hasValidStores = false;
    let totalPresent = 0;
    let totalItems = 0;

    // Obtenir les points d'intervention uniques
    const uniqueStores = [...new Set(data.map(item => item.interventionPoint))];

    // Trier alphabétiquement
    const sortedStores = uniqueStores.sort();

    // Initialiser la structure: magasin > jour > statut du produit (optimisé)
    sortedStores.forEach(store => {
      map[store] = {};
      storeInfo[store] = {
        notInStoreRange: true,
        hasChanges: false,
        previousStatus: null
      };

      // Initialiser toutes les dates avec null
      days.forEach(day => {
        map[store][format(day, 'yyyy-MM-dd')] = null;
      });
    });

    // Séparer les données en deux catégories : dans la période et avant la période
    const startDateObj = safeParseDate(startDate);
    const endDateObj = safeParseDate(endDate);

    const dataInPeriod = [];
    const dataBeforePeriod = [];

    data.forEach(item => {
      const dateObj = safeParseDate(item.date);
      if (!dateObj) {
        console.warn('Date invalide ignorée:', item.date);
        return;
      }

      if (dateObj >= startDateObj && dateObj <= endDateObj) {
        dataInPeriod.push(item);
      } else if (dateObj < startDateObj) {
        dataBeforePeriod.push(item);
      }
    });

    // Premier passage : établir le statut initial de chaque magasin avec les données antérieures
    const initialStatus = {};
    dataBeforePeriod.forEach(item => {
      const store = item.interventionPoint;
      const dateObj = safeParseDate(item.date);

      if (!initialStatus[store] || dateObj > safeParseDate(initialStatus[store].date)) {
        // Garder la mesure la plus récente avant startDate pour chaque magasin
        initialStatus[store] = {
          isPresent: item.isPresent,
          isVisitDay: false, // Ce sont des données propagées depuis avant la période
          reason: item.reason,
          reasonCategory: item.reasonCategory,
          date: item.date,
          productName: item.productName
        };
      }

      // Marquer le magasin comme valide s'il a des données antérieures non "Not in Store Range"
      if (item.reasonCategory !== 'Not in Store Range') {
        storeInfo[store].notInStoreRange = false;
        hasValidStores = true;
      }
    });

    // Deuxième passage : remplir les jours avec des données de la période
    const storeVisitDays = {};
    dataInPeriod.forEach(item => {
      const dateObj = safeParseDate(item.date);
      if (!dateObj) return;

      const dateKey = format(dateObj, 'yyyy-MM-dd');
      const store = item.interventionPoint;

      if (!map[store]) return;

      // Enregistrer cette date comme jour de visite
      if (!storeVisitDays[store]) {
        storeVisitDays[store] = [];
      }
      storeVisitDays[store].push(dateKey);

      map[store][dateKey] = {
        isPresent: item.isPresent,
        isVisitDay: true,
        reason: item.reason,
        reasonCategory: item.reasonCategory,
        date: item.date,
        productName: item.productName
      };

      // Statistiques globales (seulement pour les données dans la période)
      if (item.reasonCategory !== 'Not in Store Range') {
        storeInfo[store].notInStoreRange = false;
        hasValidStores = true;
        totalItems++;
        if (item.isPresent) {
          totalPresent++;
        }
      }
    });

    // Troisième passage: propager les statuts pour les jours sans mesure
    sortedStores.forEach(store => {
      if (storeInfo[store].notInStoreRange) return;

      // Commencer avec le statut initial si disponible
      let lastKnownStatus = initialStatus[store] || null;
      const visitDays = storeVisitDays[store] || [];

      // Trier les jours chronologiquement
      const sortedDays = days.map(day => format(day, 'yyyy-MM-dd')).sort();

      // Parcourir tous les jours de la période
      sortedDays.forEach(dateKey => {
        if (map[store][dateKey]) {
          // On a une mesure pour ce jour, mettre à jour le dernier statut connu
          lastKnownStatus = map[store][dateKey];
        } else if (lastKnownStatus) {
          // Jour sans mesure, mais on a un statut précédent, on le propage
          map[store][dateKey] = {
            ...lastKnownStatus,
            isVisitDay: false
          };
        }
      });
    });

    // Détection des changements d'état
    sortedStores.forEach(store => {
      if (storeInfo[store].notInStoreRange) return;

      // Commencer avec le statut initial comme référence
      let prevStatus = initialStatus[store] || null;
      let hasChanges = false;

      // Trier les jours chronologiquement
      const sortedDays = days.map(day => format(day, 'yyyy-MM-dd')).sort();

      for (const dateKey of sortedDays) {
        const status = map[store][dateKey];
        if (!status) continue;

        if (prevStatus && status.isVisitDay) {
          // On ne considère les changements que lors des jours de visite
          if (
            status.isPresent !== prevStatus.isPresent ||
            (status.reasonCategory !== prevStatus.reasonCategory &&
             (status.reasonCategory === 'Not in Store Range' || prevStatus.reasonCategory === 'Not in Store Range'))
          ) {
            hasChanges = true;
            break;
          }
        }

        if (status.isVisitDay) {
          prevStatus = status;
        }
      }

      storeInfo[store].hasChanges = hasChanges;
    });

    // Calculer le taux de présence global
    const dnRate = totalItems > 0 ? (totalPresent / totalItems) * 100 : 0;

    return {
      presenceMap: map,
      storesInfo: storeInfo,
      validStoresExist: hasValidStores,
      globalStats: {
        present: totalPresent,
        total: totalItems,
        rate: dnRate.toFixed(1)
      }
    };
  }, [data, days]);

  // Créer une structure pour les groupes de mois et jours
  const monthGroups = useMemo(() => {
    if (!days.length) return [];

    const groups = [];
    let currentMonth = null;
    let currentGroup = null;

    days.forEach(day => {
      const month = format(day, 'MMMM yyyy', { locale: fr });

      if (month !== currentMonth) {
        currentMonth = month;
        currentGroup = {
          month,
          days: []
        };
        groups.push(currentGroup);
      }

      currentGroup.days.push(day);
    });

    return groups;
  }, [days]);

  // Déterminer si nous avons des magasins avec des variations
  const hasStoresWithChanges = useMemo(() => {
    if (!storesInfo) return false;
    return Object.values(storesInfo).some(info => !info.notInStoreRange && info.hasChanges);
  }, [storesInfo]);

  // Filtrer les magasins selon les critères
  const filteredStores = useMemo(() => {
    if (!storesInfo) return [];

    return Object.keys(storesInfo)
      .filter(store => {
        // Exclure les magasins entièrement hors référencement
        if (storesInfo[store].notInStoreRange) return false;

        // Filtrer selon le paramètre d'affichage des magasins sans variation
        if (!showUnchangedStores && !storesInfo[store].hasChanges) return false;

        return true;
      })
      .sort();
  }, [storesInfo, showUnchangedStores]);

  // Extraire les produits uniques pour l'affichage des légendes
  const products = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map(item => item.productName))];
  }, [data]);

  // Fonction pour obtenir la classe de couleur selon le statut
  const getCellColorClass = useCallback((status) => {
    if (!status) return 'bg-gray-100 dark:bg-gray-700'; // Pas de données

    if (status.reasonCategory === 'Not in Store Range') {
      return 'bg-gray-300 dark:bg-gray-600'; // Hors référencement
    }

    return status.isPresent
      ? 'bg-green-500 dark:bg-green-600' // Présent
      : 'bg-red-500 dark:bg-red-600';    // Absent
  }, []);

  // Mettre à jour la plage visible lors du défilement
  const updateVisibleRange = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { scrollTop, scrollLeft, clientHeight, clientWidth } = container;

    // Calculer les lignes visibles (avec buffer)
    const rowHeight = 32; // Hauteur approximative d'une ligne en pixels
    const colWidth = 24;  // Largeur approximative d'une colonne en pixels

    const startRow = Math.max(0, Math.floor(scrollTop / rowHeight) - 10);
    const endRow = Math.min(
      filteredStores.length,
      Math.ceil((scrollTop + clientHeight) / rowHeight) + 10
    );

    const startCol = Math.max(0, Math.floor(scrollLeft / colWidth) - 10);
    const endCol = Math.min(
      days.length,
      Math.ceil((scrollLeft + clientWidth) / colWidth) + 10
    );

    setVisibleRange({ startRow, endRow, startCol, endCol });
  }, [days.length, filteredStores.length]);

  // Ajouter un gestionnaire de défilement
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      window.requestAnimationFrame(updateVisibleRange);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    updateVisibleRange(); // Initial update

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [updateVisibleRange]);

  // Sélectionner les jours visibles
  const visibleDays = useMemo(() => {
    return days.slice(visibleRange.startCol, visibleRange.endCol);
  }, [days, visibleRange.startCol, visibleRange.endCol]);

  // Sélectionner les magasins visibles
  const visibleStores = useMemo(() => {
    return filteredStores.slice(visibleRange.startRow, visibleRange.endRow);
  }, [filteredStores, visibleRange.startRow, visibleRange.endRow]);

  // Préparer les en-têtes de mois visibles
  const visibleMonthGroups = useMemo(() => {
    if (visibleDays.length === 0) return [];

    const groups = [];
    let currentMonth = null;
    let currentGroup = null;
    let startIndex = 0;

    visibleDays.forEach((day, index) => {
      const month = format(day, 'MMMM yyyy', { locale: fr });

      if (month !== currentMonth) {
        if (currentGroup) {
          currentGroup.width = (index - startIndex) * 24;
        }

        currentMonth = month;
        startIndex = index;
        currentGroup = {
          month,
          startIndex,
          width: 0
        };
        groups.push(currentGroup);
      }
    });

    // Dernière groupe
    if (currentGroup) {
      currentGroup.width = (visibleDays.length - startIndex) * 24;
    }

    return groups;
  }, [visibleDays]);

  // Fonction pour calculer les styles de couleur de la DN globale
  const getDNColorStyle = (rate) => {
    const dnRate = parseFloat(rate);
    if (dnRate >= 90) return 'bg-green-500 text-white';
    if (dnRate >= 75) return 'bg-blue-500 text-white';
    if (dnRate >= 50) return 'bg-yellow-500 text-white';
    if (dnRate >= 25) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  // Afficher un message si aucune donnée initiale n'est disponible
  if (!data || data.length === 0 || !validStoresExist) {
    return (
      <div className="bg-white dark:bg-gray-800 p-8 rounded shadow flex flex-col items-center justify-center text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
          {_t('No data available')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {_t('There is no presence data for the selected period')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 text-left">
              {_t('Product presence based on visits for the selected period')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-left">
              {days.length} {_n('day', 'days', days.length)} · {filteredStores.length} {_n('store', 'stores', filteredStores.length)} · {products.length} {_n('product', 'products', products.length)}
            </p>
          </div>
        </div>

        {/* Filtre pour afficher/masquer les magasins sans variation, seulement si nécessaire */}
        {hasStoresWithChanges && (
          <div className="mt-3 flex items-center">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showUnchangedStores}
                onChange={() => setShowUnchangedStores(!showUnchangedStores)}
                className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {_t('Show stores without variation on the period')}
              </span>
            </label>
          </div>
        )}
      </div>

      {filteredStores.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {_t('No store to display with the current filters')}
          </p>
          <button
            onClick={() => setShowUnchangedStores(true)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            {_t('Show all stores')}
          </button>
        </div>
      ) : (
        <div className="relative" ref={gridRef}>
          {/* Tooltip pour les détails au survol */}
          {hoveredCell && (
            <div
              className="absolute z-10 bg-gray-800 text-white p-2 rounded shadow-lg text-xs"
              style={{
                top: `${hoveredCell.top + 30}px`,
                left: `${hoveredCell.left}px`
              }}
            >
              <p className="font-bold">{hoveredCell.store}</p>
              <p>Date: {safeFormat(hoveredCell.date, 'dd/MM/yyyy', { locale: fr })}</p>
              {hoveredCell.status && <p>Produit: {hoveredCell.status.productName}</p>}
              <p>Statut: {hoveredCell.status ? (hoveredCell.status.isPresent ? _t('Present') : _t('Absent')) : _t('Unknown')}</p>
              {hoveredCell.status && !hoveredCell.status.isPresent && (
                <p>Raison: {hoveredCell.status.reason || _t('Not specified')}</p>
              )}
              {hoveredCell.status && (
                <p>Jour de visite: {hoveredCell.status.isVisitDay ? _t('Yes') : _t('No')}</p>
              )}
              {hoveredCell.status && !hoveredCell.status.isVisitDay && (
                <p><i>
                  {safeParseDate(hoveredCell.status.date) < safeParseDate(startDate)
                    ? _t('Value propagated from before the period')
                    : _t('Value propagated from last visit')}
                </i></p>
              )}
            </div>
          )}

          {/* Légende */}
          <div className="flex items-center gap-4 p-3 text-xs flex-wrap">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Present')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Absent')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-300 dark:bg-gray-500 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Not in Store Range')}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-100 dark:bg-gray-700 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('No data')}</span>
            </div>
            <div className="flex items-center ml-4">
              <div className="w-3 h-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 mr-2 flex items-center justify-center">
                <div className="w-1 h-1 bg-black dark:bg-white rounded-full"></div>
              </div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Visit day')}</span>
            </div>
            <div className="flex items-center ml-4">
              <div className="w-3 h-3 bg-green-500 opacity-70 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Value propagated in the period')}</span>
            </div>
            <div className="flex items-center ml-4">
              <div className="w-3 h-3 bg-green-500 opacity-50 mr-2"></div>
              <span className="text-gray-800 dark:text-gray-200">{_t('Value propagated from before the period')}</span>
            </div>
          </div>

          {/* Grille principale avec conteneur pour le scroll */}
          <div
            className="overflow-auto max-h-[60vh]"
            style={{ position: 'relative' }}
            ref={scrollContainerRef}
          >
            <div
              className="min-w-max"
              style={{
                height: `${filteredStores.length * 32 + 64}px`, // +64 pour l'en-tête
                width: `${48 + days.length * 24}px`
              }}
            >
              {/* En-tête des colonnes - Mois et jours (sticky) */}
              <div className="sticky top-0 z-10 bg-white dark:bg-gray-800">
                <div className="flex">
                  {/* Cellule vide pour l'alignement avec les noms des magasins (sticky horizontalement) */}
                  <div className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky left-0 z-20"></div>

                  {/* Entêtes des mois - Seulement ceux visibles */}
                  <div className="flex" style={{ marginLeft: `${visibleRange.startCol * 24}px` }}>
                    {visibleMonthGroups.map((group, groupIdx) => (
                      <div
                        key={groupIdx}
                        className="border-r border-gray-200 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800"
                        style={{ width: `${group.width}px` }}
                      >
                        {group.month}
                      </div>
                    ))}
                  </div>
                </div>

                {/* En-tête des jours - Seulement ceux visibles */}
                <div className="flex border-t border-gray-200 dark:border-gray-700">
                  <div className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky left-0 z-20"></div>
                  <div className="flex absolute" style={{ left: '48px', top: '0', marginLeft: `${visibleRange.startCol * 24}px` }}>
                    {visibleDays.map((day, idx) => (
                      <div
                        key={visibleRange.startCol + idx}
                        className="w-6 h-8 text-center text-xs flex-shrink-0 border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center bg-white dark:bg-gray-800"
                      >
                        {format(day, 'd')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lignes de magasins - Utilisation de position absolute pour optimiser les performances */}
              {visibleStores.map((store, storeIndex) => {
                const absoluteRowIndex = visibleRange.startRow + storeIndex;
                return (
                  <div
                    key={store}
                    className="absolute w-full border-t border-gray-200 dark:border-gray-700"
                    style={{ top: `${(visibleRange.startRow + storeIndex) * 32 + 64}px`, height: '32px' }}
                  >
                    {/* Nom du magasin (sticky horizontalement) */}
                    <div className="w-48 p-2 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 truncate text-gray-800 dark:text-gray-200 sticky left-0 z-10 bg-white dark:bg-gray-800 absolute h-full">
                      {store}
                    </div>

                    {/* Cellules pour chaque jour - Seulement celles visibles */}
                    {visibleDays.map((day, dayIndex) => {
                      const absoluteColIndex = visibleRange.startCol + dayIndex;
                      const dateKey = format(day, 'yyyy-MM-dd');
                      const status = presenceMap[store] && presenceMap[store][dateKey];
                      const colorClass = getCellColorClass(status);

                      // Classe d'opacité pour distinguer visuellement les valeurs propagées
                      let opacityClass = '';
                      if (status && !status.isVisitDay) {
                        // Si la donnée provient d'avant la période, utiliser une opacité plus faible
                        if (safeParseDate(status.date) < safeParseDate(startDate)) {
                          opacityClass = 'opacity-50';
                        } else {
                          opacityClass = 'opacity-70';
                        }
                      }

                      return (
                        <div
                          key={dateKey}
                          className={`w-6 h-6 flex-shrink-0 border-r border-gray-200 dark:border-gray-700
                                    ${colorClass} ${opacityClass} flex items-center justify-center cursor-pointer absolute`}
                          style={{
                            left: `${48 + absoluteColIndex * 24}px`,
                            top: '3px'  // Centrer verticalement dans la ligne
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const gridRect = gridRef.current.getBoundingClientRect();

                            setHoveredCell({
                              top: rect.top - gridRect.top,
                              left: rect.left - gridRect.left,
                              store,
                              date: day,
                              status
                            });
                          }}
                          onMouseLeave={() => setHoveredCell(null)}
                          title={format(day, 'EEEE dd/MM/yyyy', { locale: fr })}
                        >
                          {status && status.isVisitDay && (
                            <div className="w-1 h-1 bg-black dark:bg-white rounded-full"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DNProductPresence;